// Super admin: features built for gpt.bidlight.com requests that BidLight
// could not answer at the time (owner 2026-11-01). Each row is tagged with
// the person, company, project and time of the request, links to the
// delivered artifact and the chat, and can be shipped to prod or rejected.
// Data: back/routes/featureBuilds.js.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Tag, Button, Select, Input, Modal, Space, Tooltip, message } from "antd";
import TopNavbar from "pages/configuration/navbar";
import Sidebar from "pages/configuration/sidebar";
import { get, post } from "api/index";

const STATUS_COLOR = {
  queued: "default", building: "processing", built: "blue", failed: "red",
  ship_requested: "gold", shipping: "processing", shipped: "green", rejected: "default",
};
const STATUS_LABEL = {
  queued: "Queued", building: "Building", built: "Built on staging", failed: "Failed",
  ship_requested: "Ship approved", shipping: "Shipping", shipped: "On prod", rejected: "Rejected",
};
const when = (d) => (d ? new Date(d).toLocaleString() : "");

export default function FeatureBuilds() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    get(`/feature-builds${status ? `?status=${status}` : ""}`)
      .then((r) => setRows((r && r.builds) || []))
      .catch((e) => message.error((e && e.err) || "Could not load builds"))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // The delivered page, fetched with the session and opened in a new tab.
  const openArtifact = (row) => {
    const w = window.open("", "_blank");
    get(`/feature-builds/${row._id}/artifact`)
      .then((r) => { const url = URL.createObjectURL(new Blob([r.html], { type: "text/html" })); if (w) w.location = url; else window.location.href = url; })
      .catch((e) => { if (w) w.close(); message.error((e && e.err) || "No artifact yet"); });
  };

  const act = (row, what) => {
    const go = (note) => post(`/feature-builds/${row._id}/${what}`, note ? { note } : {})
      .then(() => { message.success(what === "ship" ? "Approved for prod - the builder ships it next" : "Rejected"); load(); })
      .catch((e) => message.error((e && e.err) || "Could not update"));
    if (what === "ship") {
      Modal.confirm({ title: `Ship "${row.title}" to app.bidlight.com?`, content: "The builder cherry-picks the staging commit onto prod, rebuilds and restarts.", okText: "Ship to prod", onOk: () => go() });
    } else {
      let note = "";
      Modal.confirm({ title: `Reject "${row.title}"?`, content: <Input.TextArea rows={3} placeholder="Why (optional)" onChange={(e) => { note = e.target.value; }} />, okText: "Reject", okButtonProps: { danger: true }, onOk: () => go(note) });
    }
  };

  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.title, r.request, r.companyName, r.projectTitle, r.requestedBy && r.requestedBy.email].some((x) => String(x || "").toLowerCase().includes(s)));
  }, [rows, search]);

  const columns = [
    { title: "Requested", dataIndex: "createdAt", width: 150, render: when, sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt), defaultSortOrder: "descend" },
    { title: "Feature", dataIndex: "title", render: (t, r) => <a onClick={() => setOpen(r)}>{t}</a> },
    { title: "User", width: 190, render: (r) => (r.requestedBy ? <Tooltip title={r.requestedBy.email}>{r.requestedBy.name || r.requestedBy.email}</Tooltip> : "") },
    { title: "Company", dataIndex: "companyName", width: 150 },
    { title: "Project", dataIndex: "projectTitle", width: 170 },
    { title: "Status", dataIndex: "status", width: 140, render: (s) => <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s] || s}</Tag> },
    { title: "Delivered", width: 190, render: (r) => (
      <Space size={4} wrap>
        {["built", "ship_requested", "shipping", "shipped"].includes(r.status) ? <a onClick={() => openArtifact(r)}>Artifact</a> : null}
        {r.conversationUrl ? <a href={r.conversationUrl} target="_blank" rel="noopener noreferrer">Chat</a> : null}
        {r.stagingCommit ? <Tooltip title={`staging ${r.stagingCommit}`}><Tag>{r.stagingCommit.slice(0, 9)}</Tag></Tooltip> : null}
      </Space>
    ) },
    { title: "", width: 170, render: (r) => (
      <Space size={4}>
        {r.status === "built" ? <Button size="small" type="primary" onClick={() => act(r, "ship")}>Ship to prod</Button> : null}
        {["queued", "built", "failed", "ship_requested"].includes(r.status) ? <Button size="small" danger onClick={() => act(r, "reject")}>Reject</Button> : null}
      </Space>
    ) },
  ];

  return (
    <div className="personal-area">
      <TopNavbar handleSearch={() => {}} />
      <div className="content-bg p-2">
        <div className="container d-flex mt-1">
          <Sidebar />
          <div className="profile-content px-3 pt-3" style={{ minWidth: 0, flex: 1 }}>
            <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
              <div>
                <div className="font-weight-bold text-black" style={{ fontSize: 16 }}>Built features</div>
                <div style={{ color: "#6b7c8c", fontSize: 12.5 }}>Features BidLight GPT built because they did not exist when a user asked. Built on staging; ship to prod from here.</div>
              </div>
              <Space wrap>
                <Input.Search allowClear placeholder="Search" onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
                <Select value={status} onChange={setStatus} style={{ width: 170 }} options={[{ value: "", label: "All statuses" }, ...Object.keys(STATUS_LABEL).map((k) => ({ value: k, label: STATUS_LABEL[k] }))]} />
                <Button onClick={load}>Refresh</Button>
              </Space>
            </div>
            <Table rowKey="_id" size="small" loading={loading} columns={columns} dataSource={shown} pagination={{ pageSize: 25 }} scroll={{ x: 1100 }} />
          </div>
        </div>
      </div>
      <Modal open={!!open} onCancel={() => setOpen(null)} footer={null} width={760} title={open ? open.title : ""}>
        {open ? (
          <div style={{ fontSize: 13 }}>
            <p><b>Asked by</b> {open.requestedBy && (open.requestedBy.name || open.requestedBy.email)} ({open.companyName}) on {when(open.createdAt)}{open.projectTitle ? <> - project <b>{open.projectTitle}</b></> : null}</p>
            <p><b>Request</b><br />{open.request}</p>
            {open.spec ? <p><b>What was built</b><br /><span style={{ whiteSpace: "pre-wrap" }}>{open.spec}</span></p> : null}
            {open.outputSummary ? <p><b>Output</b><br />{open.outputSummary}</p> : null}
            {open.filesChanged && open.filesChanged.length ? <p><b>Files</b><br />{open.filesChanged.join(", ")}</p> : null}
            {open.lastError ? <p style={{ color: "#a33" }}><b>Error</b><br />{open.lastError}</p> : null}
            <p><a onClick={() => openArtifact(open)}>Open the delivered artifact</a></p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
